import { useQuery } from '@tanstack/react-query';
import { apiClient, testUserHeaders } from '../client';
import { queryKeys } from '../queryKeys';
import {
  getIdToken,
  isFirebaseConfigured,
  waitForFirebaseAuthReady,
} from '@/lib/firebase';
import { createSseParser } from '@/lib/sse';
import { useAuthStore } from '@/stores/auth-store';

export interface AiAssistantAccess {
  allowed: boolean;
  configured?: boolean;
  model?: string;
  specSections?: number;
  tables?: number;
}

/** Whether the signed-in user may use the assistant (drives nav + page). */
export function useAiAssistantAccess(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.aiAssistant.access(),
    queryFn: async () => {
      const { data } = await apiClient.get<AiAssistantAccess>(
        '/api/ai-assistant/access',
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

export interface AssistantToolStart {
  id: string;
  name: string;
  input: string;
}

export interface AssistantToolResult {
  id: string;
  name: string;
  ok: boolean;
  summary: string;
  durationMs: number;
}

export interface AssistantDone {
  stopReason: string | null;
  rounds: number;
  durationMs: number;
  usage: {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
  };
}

export interface AssistantStreamHandlers {
  onText(delta: string): void;
  /** Narration the model wrote before a tool call; belongs in the trail. */
  onNote(text: string): void;
  /** Which model is answering (sent once at the start of the turn). */
  onMeta?(meta: { model: string; configured: boolean }): void;
  onThinking(delta: string): void;
  onToolStart(event: AssistantToolStart): void;
  onToolResult(event: AssistantToolResult): void;
  onRound(round: number): void;
  onDone(event: AssistantDone): void;
  onError(message: string): void;
}

const TEST_USER_ID =
  (import.meta as unknown as { env: Record<string, string> }).env
    .VITE_TEST_USER_ID || 'd3913fb6-82c9-46d7-bdcf-fa1bd0c0dd44';

/** Waits before each retry of a send that never reached the server. */
export const NETWORK_RETRY_DELAYS_MS = [1_000, 3_000];

export const CONNECTION_LOST_BEFORE_START =
  'Lost the connection before the answer started — usually a signal drop. Tap Retry to send the question again.';
export const CONNECTION_LOST_MID_ANSWER =
  'The connection dropped while the answer was coming in. Tap Retry to ask again.';

/**
 * Waits `ms`, but no longer than it takes the phone to come back online, and
 * stops early if the send is cancelled.
 */
function waitForRetry(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    window.addEventListener('online', done);
    signal.addEventListener('abort', done);
  });
}

/**
 * Streams one assistant turn over SSE. Auth mirrors the apiClient
 * interceptor's tier order (WMS session token → dev test-user → Firebase),
 * same as reevaluateAllTagsSSE. No warehouse/channel headers — the assistant
 * is not warehouse-scoped.
 */
export async function streamAssistantChat(
  messages: { role: 'user' | 'assistant'; content: string }[],
  handlers: AssistantStreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  const API_URL =
    (import.meta as unknown as { env: Record<string, string> }).env
      .VITE_API_URL || '';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  const { wmsToken, wmsTokenExpiresAt } = useAuthStore.getState();
  const wmsValid =
    !!wmsToken &&
    (!wmsTokenExpiresAt || new Date(wmsTokenExpiresAt).getTime() > Date.now());

  if (wmsValid && wmsToken) {
    headers['Authorization'] = `Bearer ${wmsToken}`;
  } else if (import.meta.env.DEV && !isFirebaseConfigured() && TEST_USER_ID) {
    Object.assign(headers, testUserHeaders());
  } else {
    try {
      await waitForFirebaseAuthReady();
      const token = await getIdToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // proceed without token — the server will 401
    }
  }

  // Phones drop and regain signal (or wake a backgrounded tab) all the time;
  // a send that dies before the server answers is retried quietly. Nothing
  // has run server-side in that case, and the assistant is read-only anyway.
  let response: Response | null = null;
  for (let attempt = 0; ; attempt++) {
    try {
      response = await fetch(`${API_URL}/api/ai-assistant/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages }),
        signal,
      });
      break;
    } catch {
      if (signal.aborted) return;
      if (attempt >= NETWORK_RETRY_DELAYS_MS.length) {
        handlers.onError(CONNECTION_LOST_BEFORE_START);
        return;
      }
      await waitForRetry(NETWORK_RETRY_DELAYS_MS[attempt], signal);
      if (signal.aborted) return;
    }
  }

  if (!response.ok || !response.body) {
    handlers.onError(
      response.status === 403
        ? 'You do not have access to the assistant.'
        : `Request failed: ${response.status} ${response.statusText}`,
    );
    return;
  }

  const parse = createSseParser(({ event, data }) => {
    let payload: unknown;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    switch (event) {
      case 'text':
        handlers.onText((payload as { delta: string }).delta ?? '');
        break;
      case 'note':
        handlers.onNote((payload as { text: string }).text ?? '');
        break;
      case 'meta':
        handlers.onMeta?.(payload as { model: string; configured: boolean });
        break;
      case 'thinking':
        handlers.onThinking((payload as { delta: string }).delta ?? '');
        break;
      case 'tool_start':
        handlers.onToolStart(payload as AssistantToolStart);
        break;
      case 'tool_result':
        handlers.onToolResult(payload as AssistantToolResult);
        break;
      case 'round':
        handlers.onRound((payload as { round: number }).round ?? 0);
        break;
      case 'done':
        handlers.onDone(payload as AssistantDone);
        break;
      case 'error':
        handlers.onError(
          (payload as { message: string }).message ?? 'Unknown error',
        );
        break;
      default:
        break; // meta + future events
    }
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parse(decoder.decode(value, { stream: true }));
    }
  } catch {
    if (!signal.aborted) handlers.onError(CONNECTION_LOST_MID_ANSWER);
  }
}

export interface AssistantFeedbackInput {
  rating: 'UP' | 'DOWN';
  question: string;
  answer: string;
  note?: string;
  conversationId?: string;
  messageId?: string;
  /** The model that produced the answer (from the stream's meta event). */
  model?: string;
}

/** Thumbs up/down on an answer; the server freezes question + answer. */
export async function sendAssistantFeedback(
  input: AssistantFeedbackInput,
): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(
    '/api/ai-assistant/feedback',
    input,
  );
  return data;
}
